// commit-analytics-event-choice.ts — PSTUDIO-4784
import { NodeColumns, tableStore } from '../../../stores/table-store';
import {
  buildAnalyticsEventText,
  readAnalyticsEventName,
} from './analytics-events';
import { buildPanelCellCommitUpdate } from './panel-cell-updates';

/**
 * Make `name` the ONE event of an Analytics Events cell, replacing whatever the
 * cell held (the ticket: a choice replaces the content, so a typo in a library
 * name cannot leave an event uncollected). Returns whether anything was written.
 *
 * The write goes through `recordRowEdit`, the path the panel's other pickers
 * (`commitCommandChange`, `commitAssetTypeChange`) use, so a choice is one
 * history step: Undo puts back the previous text AND its key-order map. The
 * update itself is `buildPanelCellCommitUpdate`, the panel's own commit, so the
 * text and the `analyticsEventMap` beside it are canonicalized the way a typed
 * edit of this cell is — not by a second copy of that rule.
 *
 * Nothing is written for an empty name, nor when the cell already shows this
 * event NAME (`readAnalyticsEventName`), whatever else the cell holds: re-picking
 * the current event must not add a history step, and must not discard the fields
 * the user added to it. Limit: a cell that holds only the name, written before a
 * library event carried its suggested properties, therefore cannot be given the
 * template by picking the same event again; pick another event first.
 *
 * It does not flush a pending panel edit session. The panel's own select
 * does it before calling (`chooseAnalyticsEvent` runs `commitPanelEditorValue`
 * first, so a typed draft is committed as its own step). The grid cell's select
 * does NOT, and both can be live at once: the panel is open on the selected cell,
 * and that cell's select is mounted. What closes the gap in the usual path is the
 * editor's blur — focus or a click moving from the panel to the grid's select
 * lands outside the panel, and `handleEditorBlur` commits the draft then
 * (`shouldCommitPanelEditorBlur`), before the pick. A click INSIDE the panel does
 * not commit on blur, which is why the panel's own select flushes by hand. No
 * test covers the grid-cell order; it was read from the code only.
 */
export function commitAnalyticsEventChoice(
  rowId: string,
  name: string
): boolean {
  const text = buildAnalyticsEventText(name);
  if (text === null) return false;
  if (
    readAnalyticsEventName(
      tableStore.getValue(rowId, NodeColumns.analyticsEvent)
    ) === name.trim()
  ) {
    return false;
  }
  tableStore.recordRowEdit(
    rowId,
    'Change analytics event',
    () => {
      tableStore.updateRow({
        rowId,
        updates: buildPanelCellCommitUpdate(
          NodeColumns.analyticsEvent,
          text,
          true
        ),
        skipValidationRules: false,
        saveImmediately: true,
      });
      tableStore.clearCellValidationError(rowId, NodeColumns.analyticsEvent);
    },
    NodeColumns.analyticsEvent
  );
  return true;
}
